import { Briefcase, Factory, Globe, Ship, Wheat } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Overline, Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { Marquee } from "@/components/Marquee";

const buyerGroups = [
    { icon: Factory, title: "Feed Mills", text: "Compound feed producers running DOC as a core protein ingredient in cattle and poultry formulations." },
    { icon: Globe, title: "Commodity Importers", text: "Import houses moving containerized agri-commodities into regional distribution networks." },
    { icon: Wheat, title: "Livestock Feed Manufacturers", text: "Integrated livestock and dairy operations manufacturing feed for their own herds and contract farms." },
    { icon: Ship, title: "Trading Companies", text: "Commodity traders building positions in Indian oilseed meals for onward sale." },
    { icon: Briefcase, title: "Procurement Houses", text: "Mandated buyers sourcing feed raw materials on behalf of mills, integrators, and government programs." },
];

const regions = [
    { name: "Asia", text: "Priority markets include Vietnam and Bangladesh, alongside other regional feed hubs." },
    { name: "Middle East", text: "Active demand from Gulf feed mills and trading houses, with the UAE as a priority market." },
    { name: "Africa", text: "Growing compound-feed markets served via west Indian ports with competitive freight." },
    { name: "Europe", text: "Specialty and trading demand handled with full export documentation and inspection support." },
];

export default function Markets() {
    return (
        <>
            <Seo
                title="Export Markets & Buyer Groups — Asia, Middle East, Africa, Europe | AXORA PS GLOBAL LLP"
                description="AXORA PS GLOBAL LLP supplies rapeseed/mustard DOC to feed mills, commodity importers, livestock feed manufacturers, and trading companies across Asia, the Middle East, Africa, and Europe."
                path="/markets"
                ogImage="/images/og-markets.jpg"
            />

            <section data-testid="markets-hero" className="relative overflow-hidden bg-white pt-[72px]">
                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-steel-light/80 via-steel-light/30 to-white" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-14 sm:px-8 lg:px-12 lg:pb-20 lg:pt-20">
                    <Reveal>
                        <Overline>Markets</Overline>
                        <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl">
                            Markets We Serve
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
                            Rapeseed/mustard DOC from Rajasthan moves to key global feed markets through west-coast
                            Indian ports — to buyers who need verified quality and paperwork that clears the first time.
                        </p>
                    </Reveal>
                </div>
            </section>

            <Marquee items={["Asia", "Middle East", "Africa", "Europe", "Vietnam", "Bangladesh", "UAE", "Key Global Feed Markets"]} />

            <section data-testid="buyer-groups" className="bg-white py-20 lg:py-28">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <Reveal>
                        <Overline>Who Buys From Us</Overline>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Buyer Groups</h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {buyerGroups.map((group, i) => (
                            <Reveal key={group.title} delay={i * 0.07} className={i === 4 ? "sm:col-span-2 lg:col-span-1" : ""}>
                                <div
                                    data-testid={`buyer-group-${group.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="h-full rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-steel/50 hover:shadow-[0_16px_40px_rgba(10,37,64,0.1)]"
                                >
                                    <group.icon className="h-7 w-7 text-steel" />
                                    <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-navy">{group.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{group.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section data-testid="regions" className="bg-mist py-20 lg:py-28">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <Reveal>
                        <Overline>Where We Ship</Overline>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Regions & Priority Markets</h2>
                        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
                            Shipments are routed through Mundra, Kandla/Deendayal, JNPT/Nhava Sheva, Pipavav, and
                            Hazira on FOB, CFR, or CIF terms to key global feed markets.
                        </p>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                        {regions.map((region, i) => (
                            <Reveal key={region.name} delay={i * 0.08}>
                                <div
                                    data-testid={`region-${region.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="h-full rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(10,37,64,0.1)]"
                                >
                                    <p className="font-display text-xs font-bold uppercase tracking-[0.2em] text-amber">{`0${i + 1}`}</p>
                                    <h3 className="mt-3 font-display text-2xl font-bold tracking-tight text-navy">{region.name}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{region.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.12} className="mt-14">
                        <img
                            src="/images/vessel-dock.jpg"
                            alt="Large container vessel at dock preparing for an international sailing from an Indian port"
                            loading="lazy"
                            className="aspect-[21/9] w-full rounded-2xl border border-slate-200 object-cover"
                        />
                    </Reveal>
                </div>
            </section>

            <CtaBand
                location="markets"
                title="Buying for Your Region?"
                body="Tell us your destination port and monthly volume. We will respond with current pricing, freight options on FOB/CFR/CIF, and batch-wise COA support."
                label="Connect on WhatsApp"
            />
        </>
    );
}
